import { forwardRef } from 'react';

import type { SeverityTone } from './types';

export interface SeverityIndicatorProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone: SeverityTone;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'bar' | 'dot';
}

const toneClasses: Record<SeverityTone, string> = {
  critical: 'bg-[color:var(--color-severity-critical-solid)]',
  'severity-high': 'bg-[color:var(--color-severity-high-solid)]',
  'severity-medium': 'bg-[color:var(--color-severity-medium-solid)]',
  'severity-low': 'bg-[color:var(--color-severity-low-solid)]',
  info: 'bg-[color:var(--color-severity-info-solid)]',
};

const barSizeClasses = {
  sm: 'h-4 w-[3px]',
  md: 'h-6 w-[3px]',
  lg: 'h-8 w-1',
} as const;

const dotSizeClasses = {
  sm: 'h-1.5 w-1.5',
  md: 'h-2 w-2',
  lg: 'h-2.5 w-2.5',
} as const;

function cn(...parts: Array<string | undefined | false | null>): string {
  return parts.filter(Boolean).join(' ');
}

export const SeverityIndicator = forwardRef<HTMLSpanElement, SeverityIndicatorProps>(function SeverityIndicator(
  { tone, size = 'md', variant = 'bar', className, ...rest },
  ref,
) {
  return (
    <span
      ref={ref}
      aria-hidden={rest['aria-label'] ? undefined : true}
      className={cn(
        'inline-flex shrink-0 rounded-full',
        toneClasses[tone],
        variant === 'dot' ? dotSizeClasses[size] : barSizeClasses[size],
        className,
      )}
      {...rest}
    />
  );
});
